import React, { useCallback, useRef, useState } from 'react';
import { FocusScope, useOverlay } from 'react-aria';
import { useMergedRefs } from 'hooks';
import { isInShadow } from 'utils/dom';
import { UnstyledPopoverOverlayProps } from './types';
import useComposedPath from './useComposedPath';

/**
 * Keeps focus within the overlay.  Closes the overlay when the user interacts
 * outside or presses escape
 */
const UnstyledPopoverOverlay = React.forwardRef<
  HTMLDivElement,
  UnstyledPopoverOverlayProps
>(
  (
    {
      autoFocus,
      children,
      className,
      contain,
      isDismissable = true,
      isOpen,
      onClose,
      restoreFocus = true,
      shouldCloseOnBlur = true,
      shouldCloseOnInteractOutside,
      ...props
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLDivElement>(null);
    const [element, setElement] = useState<HTMLDivElement>();
    const isInShadowDom = !element || isInShadow(element);

    const mergedRefs = useMergedRefs([localRef, forwardedRef, setElement]);

    const { lastComposedPath } = useComposedPath({
      isDisabled: !isInShadowDom,
      ref: localRef,
    });

    const handleShouldCloseOnInteractOutsideInShadow = useCallback(
      (el: Element) => {
        const shouldCloseProp = shouldCloseOnInteractOutside?.(el) ?? false;

        return (
          !localRef.current ||
          !lastComposedPath.current.includes(localRef.current) ||
          shouldCloseProp
        );
      },
      [lastComposedPath, shouldCloseOnInteractOutside],
    );

    const { overlayProps } = useOverlay(
      {
        isOpen,
        onClose,
        isDismissable,
        shouldCloseOnBlur,
        shouldCloseOnInteractOutside: !isInShadowDom
          ? shouldCloseOnInteractOutside
          : handleShouldCloseOnInteractOutsideInShadow,
      },
      localRef,
    );

    return (
      <FocusScope {...{ autoFocus, contain, restoreFocus }}>
        <div
          {...props}
          {...overlayProps}
          className={className}
          ref={mergedRefs}
        >
          {children}
        </div>
      </FocusScope>
    );
  },
);

export default UnstyledPopoverOverlay;
